import { useState, useEffect, useRef, type ReactNode } from 'react';

import {
  motion,
  AnimatePresence,
  useInView,
  useAnimation,
} from 'framer-motion';
import {
  ArrowRight,
  Shield,
  Users,
  TrendingUp,
  Check,
  Menu,
  X,
  ChevronRight,
  FileText,
  Lock,
  Search,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';


// Import your logo SVGs
import logoFull from '../assets/loanguard_full.svg';
import logoIcon from '../assets/loanguard_logo_only.svg';
import { Logo } from '../components/Logo';

interface AnimatedSectionProps {
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
}

// Section component that animates when in view
const AnimatedSection = ({
  children,
  className = '',
  delay = 0,
  id,
}: AnimatedSectionProps) => {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    }
  }, [isInView, controls]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
            delay,
          },
        },
      }}
      className={className}
      id={id}
    >
      {children}
    </motion.section>
  );
};

interface FeatureCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  delay?: number;
}

// Feature card component with hover effects
const FeatureCard = ({
  icon,
  title,
  description,
  delay = 0,
}: FeatureCardProps) => (
    <motion.div
      className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 h-full hover:shadow-lg transition-shadow"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay,
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1],
      }}
      whileHover={{ y: -5, transition: { duration: 0.2 } }}
    >
      <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-5">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </motion.div>
  );

// Testimonial component
interface TestimonialProps {
  quote: string;
  author: string;
  company: string;
  delay?: number;
}

const Testimonial = ({
  quote,
  author,
  company,
  delay = 0,
}: TestimonialProps) => (
    <motion.div
      className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.5 }}
    >
      <div className="mb-4 text-primaryColor">
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.33333 16H4L9.33333 5.33333V16ZM21.3333 16H16L21.3333 5.33333V16Z"
            fill="currentColor"
          />
        </svg>
      </div>
      <p className="text-gray-700 mb-6 italic">{quote}</p>
      <div>
        <p className="font-semibold text-gray-800">{author}</p>
        <p className="text-sm text-gray-500">{company}</p>
      </div>
    </motion.div>
  );

// Stats item component
interface StatItemProps {
  number: string | number;
  label: string;
  delay?: number;
}

const StatItem = ({ number, label, delay = 0 }: StatItemProps) => (
    <motion.div
      className="text-center"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
    >
      <h3 className="text-4xl font-bold text-primaryColor mb-2">{number}</h3>
      <p className="text-gray-600">{label}</p>
    </motion.div>
  );

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  // Add header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        if (window.scrollY > 50) {
          headerRef.current.classList.add('bg-white', 'shadow-md');
          headerRef.current.classList.remove('bg-transparent');
        } else {
          headerRef.current.classList.remove('bg-white', 'shadow-md');
          headerRef.current.classList.add('bg-transparent');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle navigation to login
  const handleLoginClick = () => {
    navigate('/login');
  };

  // Handle smooth scroll
  interface ScrollToProps {
    id: string;
  }

  const scrollTo = (id: ScrollToProps['id']) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-primaryBgColor to-white">
      {/* Header */}
      <header
        ref={headerRef}
        className="fixed w-full top-0 z-50 transition-all duration-300 bg-transparent"
      >
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Use the Logo component - full on desktop, icon only on mobile */}
                <div className="hidden md:block">
                  <Logo iconSize="lg" abbreviated={false} />
                </div>
                <div className="md:hidden">
                  <Logo iconSize="md" collapsed={true} />
                </div>
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <motion.nav
              className="hidden md:flex items-center space-x-8"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <button
                onClick={() => scrollTo('about')}
                className="text-gray-700 hover:text-primaryColor transition-colors"
              >
                About
              </button>
              <button
                onClick={() => scrollTo('features')}
                className="text-gray-700 hover:text-primaryColor transition-colors"
              >
                Features
              </button>
              <button
                onClick={() => scrollTo('testimonials')}
                className="text-gray-700 hover:text-primaryColor transition-colors"
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollTo('contact')}
                className="text-gray-700 hover:text-primaryColor transition-colors"
              >
                Contact
              </button>

              <motion.button
                onClick={handleLoginClick}
                className="bg-primaryColor text-white py-2.5 px-5 rounded-lg font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Login
                <ArrowRight size={16} />
              </motion.button>
            </motion.nav>

            {/* Mobile menu button */}
            <motion.button
              className="md:hidden text-gray-700 focus:outline-none"
              onClick={() => setMobileMenuOpen(true)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Menu size={24} />
            </motion.button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 bg-white z-50 flex flex-col"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', ease: 'easeInOut', duration: 0.3 }}
          >
            <div className="p-5 flex justify-between items-center border-b">
              <img src={logoFull} alt="TDFA" className="h-10" />
              <button onClick={() => setMobileMenuOpen(false)}>
                <X size={24} className="text-gray-700" />
              </button>
            </div>
            <div className="flex-1 flex flex-col p-5 space-y-6">
              <button
                onClick={() => scrollTo('about')}
                className="py-3 px-4 text-lg text-gray-700 border-b border-gray-100 flex justify-between items-center"
              >
                About
                <ChevronRight size={20} className="text-gray-400" />
              </button>
              <button
                onClick={() => scrollTo('features')}
                className="py-3 px-4 text-lg text-gray-700 border-b border-gray-100 flex justify-between items-center"
              >
                Features
                <ChevronRight size={20} className="text-gray-400" />
              </button>
              <button
                onClick={() => scrollTo('testimonials')}
                className="py-3 px-4 text-lg text-gray-700 border-b border-gray-100 flex justify-between items-center"
              >
                Testimonials
                <ChevronRight size={20} className="text-gray-400" />
              </button>
              <button
                onClick={() => scrollTo('contact')}
                className="py-3 px-4 text-lg text-gray-700 border-b border-gray-100 flex justify-between items-center"
              >
                Contact
                <ChevronRight size={20} className="text-gray-400" />
              </button>

              <button
                onClick={handleLoginClick}
                className="mt-6 bg-primaryColor text-white py-4 px-5 rounded-lg font-medium flex items-center justify-center gap-2"
              >
                Login to Dashboard
                <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pb-32 px-4 md:px-0 relative">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Hero Left Content */}
            <div>
              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Building <span className="text-primaryColor">Trust</span> in
                Financial Collaboration
              </motion.h1>

              <motion.p
                className="text-xl text-gray-600 mb-8 max-w-xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Tirupur District Financers Association connects financiers to
                verify borrower credibility, share information, and protect
                against defaults.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <motion.button
                  onClick={handleLoginClick}
                  className="bg-primaryColor text-white py-3.5 px-8 rounded-lg font-medium flex items-center justify-center gap-2 shadow-lg shadow-primaryColor/20"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: '0 5px 20px 0 rgba(100, 130, 173, 0.35)',
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Started
                  <ArrowRight size={18} />
                </motion.button>

                <motion.button
                  onClick={() => scrollTo('about')}
                  className="bg-white text-gray-800 py-3.5 px-8 rounded-lg font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Learn More
                </motion.button>
              </motion.div>

              <motion.div
                className="mt-10 flex items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="flex -space-x-4">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center overflow-hidden"
                    >
                      <span className="text-xs font-medium text-gray-600">
                        {String.fromCharCode(65 + i)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-gray-900">50+</span>{' '}
                  financiers already joined
                </div>
              </motion.div>
            </div>

            {/* Hero Right Content - Illustration/Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-tr from-primaryColor/10 to-blue-200/30 rounded-3xl p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-primaryColor/10 rounded-full transform translate-x-16 -translate-y-16" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-200/40 rounded-full transform -translate-x-16 translate-y-16" />

                {/* Mock dashboard UI */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden z-10 relative">
                  {/* Mock header */}
                  <div className="bg-primaryColor p-4 text-white flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <img src={logoIcon} alt="Logo" className="w-8 h-8" />
                      <h3 className="font-semibold">TDFA Dashboard</h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                        <Users size={14} />
                      </span>
                      <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                        <Bell size={14} />
                      </span>
                    </div>
                  </div>

                  {/* Mock content */}
                  <div className="p-6">
                    <h4 className="text-lg font-semibold mb-4">
                      Verify Aadhaar Details
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-4 mb-4">
                      <Search className="text-gray-400" />
                      <div className="flex-1">
                        <div className="h-2.5 w-3/4 bg-gray-200 rounded animate-pulse" />
                      </div>
                      <button className="bg-primaryColor text-white px-4 py-2 rounded-lg text-sm">
                        Search
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="text-sm text-green-800 font-medium mb-1">
                          Yellow List
                        </div>
                        <div className="text-2xl font-bold">176</div>
                        <div className="text-xs text-green-700 mt-2 flex items-center">
                          <TrendingUp size={12} className="mr-1" />
                          +14% this month
                        </div>
                      </div>
                      <div className="bg-gray-900 rounded-lg p-4 text-white">
                        <div className="text-sm font-medium mb-1">
                          Black List
                        </div>
                        <div className="text-2xl font-bold">84</div>
                        <div className="text-xs text-gray-300 mt-2 flex items-center">
                          <TrendingUp size={12} className="mr-1" />
                          +5% this month
                        </div>
                      </div>
                    </div>

                    <div className="border border-gray-100 rounded-lg divide-y">
                      {[...Array(3)].map((_, i) => (
                        <div
                          key={i}
                          className="p-3 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs">
                              U{i + 1}
                            </div>
                            <div>
                              <div className="h-2.5 w-24 bg-gray-200 rounded" />
                              <div className="h-2 w-16 bg-gray-100 rounded mt-1" />
                            </div>
                          </div>
                          <div
                            className={`text-xs px-2 py-0.5 rounded-full ${i % 2 === 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-900 text-white'}`}
                          >
                            {i % 2 === 0 ? 'Yellow' : 'Black'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-1/2 right-0 w-64 h-64 bg-primaryColor/5 rounded-full transform translate-x-32 -translate-y-32 hidden lg:block" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-100/20 rounded-full transform -translate-x-40 translate-y-16 hidden lg:block" />
      </section>

      {/* Trust Indicators */}
      <AnimatedSection className="py-16 bg-white">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatItem number="50+" label="Finance Members" delay={0.1} />
            <StatItem number="5000+" label="Records Shared" delay={0.2} />
            <StatItem number="96%" label="Recovery Rate" delay={0.3} />
            <StatItem number="40%" label="Default Reduction" delay={0.4} />
          </div>
        </div>
      </AnimatedSection>

      {/* About Section */}
      <AnimatedSection className="py-24 bg-primaryBgColor" id="about">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left Side */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Uniting Financiers for a{' '}
                <span className="text-primaryColor">Secure Future</span>
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Tirupur District Financers Association was founded with a
                mission to protect the interests of financiers and promote
                ethical lending practices in our community.
              </p>

              <div className="space-y-4">
                {[
                  'Verify borrower credibility with shared data',
                  'Protect against payment defaults with early warnings',
                  'Build a community of trusted financial partners',
                  'Promote responsible lending practices',
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 * index + 0.3 }}
                  >
                    <div className="bg-green-100 p-1 rounded-full mt-1">
                      <Check size={16} className="text-green-600" />
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </motion.div>
                ))}
              </div>

              <motion.button
                onClick={() => scrollTo('features')}
                className="mt-8 flex items-center gap-2 text-primaryColor font-medium hover:text-blue-700 transition-colors"
                whileHover={{ x: 5 }}
              >
                Learn about our features
                <ArrowRight size={16} />
              </motion.button>
            </div>

            {/* Right Side */}
            <div className="relative">
              <div className="aspect-[4/3] bg-white rounded-2xl shadow-xl p-8 z-10 relative">
                <img
                  src="https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=3873&q=80"
                  alt="Financial team meeting"
                  className="w-full h-full object-cover rounded-xl"
                />

                {/* Overlay cards */}
                <motion.div
                  className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg max-w-[200px]"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-green-100 p-2 rounded-full">
                      <TrendingUp size={16} className="text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Trust Score</h4>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full mb-2">
                    <div
                      className="h-2 bg-green-500 rounded-full"
                      style={{ width: '85%' }}
                     />
                  </div>
                  <p className="text-xs text-gray-600">
                    85% of members report increased confidence in lending
                  </p>
                </motion.div>

                <motion.div
                  className="absolute -top-6 -right-6 bg-white p-4 rounded-xl shadow-lg"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Shield size={16} className="text-primaryColor" />
                    <h4 className="font-semibold text-gray-900">Protection</h4>
                  </div>
                  <p className="text-xs text-gray-600">
                    Secure data sharing between verified members
                  </p>
                </motion.div>
              </div>

              {/* Background decoration */}
              <div className="absolute inset-0 bg-gradient-to-tr from-blue-100 to-primaryColor/20 rounded-2xl transform rotate-3 -z-10" />
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Features Section */}
      <AnimatedSection className="py-24 relative" id="features">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span
              className="inline-block px-3 py-1 bg-blue-100 text-primaryColor text-sm font-medium rounded-full mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Key Features
            </motion.span>

            <motion.h2
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Everything You Need for{' '}
              <span className="text-primaryColor">Financial Security</span>
            </motion.h2>

            <motion.p
              className="text-lg text-gray-600"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Our platform offers a comprehensive suite of tools designed to
              protect financial institutions and promote responsible lending.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Search className="text-primaryColor h-6 w-6" />}
              title="Aadhaar Verification"
              description="Quickly verify borrower credibility using secure Aadhaar lookups with our comprehensive database."
              delay={0.2}
            />

            <FeatureCard
              icon={<Users className="text-primaryColor h-6 w-6" />}
              title="Member Network"
              description="Join a trusted network of financiers sharing critical information while maintaining anonymity."
              delay={0.3}
            />

            <FeatureCard
              icon={<Shield className="text-primaryColor h-6 w-6" />}
              title="Data Protection"
              description="Advanced security measures ensure all sensitive information remains protected and compliant."
              delay={0.4}
            />

            <FeatureCard
              icon={<Lock className="text-primaryColor h-6 w-6" />}
              title="Anonymous Reporting"
              description="Report defaulters without revealing your identity while helping protect the community."
              delay={0.5}
            />

            <FeatureCard
              icon={<TrendingUp className="text-primaryColor h-6 w-6" />}
              title="Risk Analytics"
              description="Access color-coded risk indicators and analytics to make informed lending decisions."
              delay={0.6}
            />

            <FeatureCard
              icon={<FileText className="text-primaryColor h-6 w-6" />}
              title="Detailed Records"
              description="Maintain comprehensive records of financial interactions and payment history."
              delay={0.7}
            />
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-1/3 left-0 w-40 h-40 bg-blue-100/40 rounded-full transform -translate-x-20 hidden lg:block" />
        <div className="absolute bottom-1/4 right-0 w-64 h-64 bg-primaryColor/5 rounded-full transform translate-x-32 hidden lg:block" />
      </AnimatedSection>

      {/* CTA Section */}
      <AnimatedSection className="py-20">
        <div className="container mx-auto max-w-6xl px-4 md:px-6">
          <div className="bg-gradient-to-r from-primaryColor to-blue-600 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="absolute left-0 top-0 h-full w-full"
                fill="none"
              >
                <g opacity="0.1">
                  <path
                    d="M0 0L100 100M50 0L150 100M100 0L200 100M150 0L250 100M200 0L300 100M250 0L350 100M300 0L400 100M350 0L450 100M400 0L500 100M450 0L550 100"
                    stroke="white"
                    strokeWidth="2"
                   />
                </g>
              </svg>
            </div>

            <div className="grid md:grid-cols-2 gap-10 items-center relative z-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Secure Your Financial Operations?
                </h2>
                <p className="text-white/80 text-lg mb-8">
                  Join the Tirupur District Financers Association today and
                  become part of a community dedicated to protecting financial
                  interests.
                </p>

                <motion.button
                  onClick={handleLoginClick}
                  className="bg-white text-primaryColor py-3.5 px-8 rounded-lg font-medium flex items-center gap-2 shadow-xl shadow-blue-900/20"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: '0 5px 20px 0 rgba(255, 255, 255, 0.25)',
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  Get Started Now
                  <ArrowRight size={18} />
                </motion.button>
              </div>

              <div className="flex justify-center md:justify-end">
                <motion.div
                  className="bg-white/10 backdrop-blur-sm p-6 rounded-xl"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="text-center px-4">
                    <div className="w-16 h-16 rounded-full bg-white/20 mx-auto flex items-center justify-center mb-4">
                      <Shield className="text-white h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">
                      Protection Guarantee
                    </h3>
                    <p className="text-white/80 mb-6">
                      Our platform has helped reduce default rates by 40% for
                      our members.
                    </p>

                    <div className="flex items-center justify-between text-sm border-t border-white/20 pt-4">
                      <span>Member satisfaction:</span>
                      <span className="font-semibold">96%</span>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Testimonials Section */}
      <AnimatedSection className="py-24 bg-primaryBgColor" id="testimonials">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.span
              className="inline-block px-3 py-1 bg-blue-100 text-primaryColor text-sm font-medium rounded-full mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Testimonials
            </motion.span>

            <motion.h2
              className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              What Our Members Are Saying
            </motion.h2>

            <motion.p
              className="text-lg text-gray-600"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Hear from finance professionals who have transformed their
              operations using our platform.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Testimonial
              quote="The TDFA platform has been instrumental in helping us identify high-risk borrowers. We've seen a 35% reduction in defaults since joining."
              author="Rajesh Kumar"
              company="SK Finance"
              delay={0.2}
            />

            <Testimonial
              quote="The ability to anonymously report defaulters while collectively protecting each other has created a much safer lending environment."
              author="Priya Sharma"
              company="Priya Finance Company"
              delay={0.3}
            />

            <Testimonial
              quote="Quick Aadhaar verification has streamlined our approval process. We now make better decisions faster, protecting our assets effectively."
              author="Mohan Venkatesh"
              company="MVR Finance"
              delay={0.4}
            />
          </div>
        </div>
      </AnimatedSection>

      {/* Contact Section */}
      <AnimatedSection className="py-20" id="contact">
        <div className="container mx-auto max-w-6xl px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Get in Touch With Us
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Have questions about joining TDFA or need more information?
                Contact us using the form or visit our office.
              </p>

              <div className="space-y-6 mb-8">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Office Address
                  </h4>
                  <p className="text-gray-600">
                    No. 391, Thangam Plaza Ground Floor, Tarapuram Road, Near
                    Usha Theatre, Tirupur - 641 604
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">Email Us</h4>
                  <p className="text-gray-600">info@tdfa.in</p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Phone Number
                  </h4>
                  <p className="text-gray-600">+91 XXXXXXXXXX</p>
                </div>
              </div>

              <div className="flex space-x-4">
                <a
                  href="#"
                  className="w-10 h-10 bg-primaryBgColor rounded-full flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 text-primaryColor"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-primaryBgColor rounded-full flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 text-primaryColor"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-primaryBgColor rounded-full flex items-center justify-center"
                >
                  <svg
                    className="w-5 h-5 text-primaryColor"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Send us a message
                </h3>

                <form className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-transparent"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-transparent"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primaryColor focus:border-transparent"
                      rows={4}
                      placeholder="How can we help you?"
                     />
                  </div>

                  <motion.button
                    type="submit"
                    className="w-full py-3 bg-primaryColor text-white rounded-lg font-medium"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Send Message
                  </motion.button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-16">
            <div className="md:col-span-2">
              <img
                src={logoFull}
                alt="TDFA"
                className="h-12 mb-4 brightness-200"
              />
              <p className="text-gray-300 mb-6 max-w-md">
                Tirupur District Financers Association unites financiers to
                foster collaboration and provide financial solutions for a
                secure ecosystem.
              </p>
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} TDFA. All rights reserved.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => scrollTo('about')}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    About Us
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo('features')}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Features
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo('testimonials')}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Testimonials
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollTo('contact')}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    Cookie Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

// Missing Bell component added for completeness
const Bell = ({ size = 24, ...props }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
